import React, { useState, useCallback, useMemo } from 'react';
import { LabelTemplate } from '@/store/label-template.store';
import { toast } from 'react-hot-toast';
import { TemplateSelectModal } from './TemplateSelectModal.tsx';

// 常量定义
const VOLUME_WEIGHT_DIVISOR = 6000; // 体积重计算系数
const MAX_EXPORT_COUNT = 1000; // 单次最多导出数量

export interface BatchLabelExportItem {
    id: string;
    tracking_no: string;
    receiver_name?: string;
    receiver_phone?: string;
    receiver_address?: string;
    shipper_name?: string;
    weight?: number;
    length?: number;
    width?: number;
    height?: number;
    created_at?: string;
    [key: string]: any;
}

export interface BatchLabelExportProps {
    items: BatchLabelExportItem[];
    batchNo: string;
    operatorName?: string;
    onExportStart?: () => void;
    onExportComplete?: (count: number) => void;
    onExportError?: (error: Error) => void;
    renderLabel?: (item: BatchLabelExportItem, template: LabelTemplate) => Promise<HTMLCanvasElement>;
    isInvalid?: (item: BatchLabelExportItem) => boolean;
    // 外部状态管理（可选）
    isSelectionMode?: boolean;
    selectedIds?: Set<string>;
    onSelectionModeChange?: (mode: boolean) => void;
    onSelectedIdsChange?: (ids: Set<string>) => void;
}

/**
 * 清理文件名中的非法字符
 */
const sanitizeFilename = (name: string): string => {
    return name
        .replace(/[\/\\:*?"<>|]/g, '_')
        .substring(0, 50);
};

/**
 * 清理用户输入
 */
const sanitizeInput = (input: string): string => {
    return input
        .replace(/[<>\"']/g, '')
        .trim()
        .substring(0, 50);
};

export const BatchLabelExport: React.FC<BatchLabelExportProps> = ({
    items,
    batchNo,
    operatorName = '',
    onExportStart,
    onExportComplete,
    onExportError,
    renderLabel,
    isInvalid = () => false,
    isSelectionMode: externalIsSelectionMode,
    selectedIds: externalSelectedIds,
    onSelectionModeChange,
    onSelectedIdsChange,
}) => {
    // 使用外部状态或内部状态
    const [internalIsSelectionMode, setInternalIsSelectionMode] = useState(false);
    const [internalSelectedIds, setInternalSelectedIds] = useState<Set<string>>(new Set());
    
    const isSelectionMode = externalIsSelectionMode !== undefined ? externalIsSelectionMode : internalIsSelectionMode;
    const selectedIds = externalSelectedIds !== undefined ? externalSelectedIds : internalSelectedIds;
    
    const setIsSelectionMode = (mode: boolean) => {
        if (onSelectionModeChange) {
            onSelectionModeChange(mode);
        } else {
            setInternalIsSelectionMode(mode);
        }
    };
    
    const setSelectedIds = useCallback((ids: Set<string> | ((prev: Set<string>) => Set<string>)) => {
        if (onSelectedIdsChange) {
            const newIds = typeof ids === 'function' ? ids(selectedIds) : ids;
            onSelectedIdsChange(newIds);
        } else {
            setInternalSelectedIds(ids as any);
        }
    }, [selectedIds, onSelectedIdsChange]);

    const [isExportingPDF, setIsExportingPDF] = useState(false);
    const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);

    // 使用 useMemo 缓存有效项目列表
    const validItems = useMemo(() => 
        items.filter(item => !isInvalid(item)),
        [items, isInvalid]
    );

    const toggleSelection = useCallback((id: string) => {
        setSelectedIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    }, [setSelectedIds]);

    // 优化：使用函数式更新避免依赖数组问题
    const toggleSelectAll = useCallback(() => {
        setSelectedIds(prev => {
            const allIds = new Set(validItems.map(item => item.id));
            return prev.size === validItems.length ? new Set() : allIds;
        });
    }, [validItems, setSelectedIds]);

    const handleExportLabelsPDF = useCallback(async (template: LabelTemplate) => {
        if (!selectedIds || selectedIds.size === 0) {
            toast.error('请先选择要导出的项目');
            return;
        }

        if (selectedIds.size > MAX_EXPORT_COUNT) {
            toast.error(`单次最多导出 ${MAX_EXPORT_COUNT} 个项目`);
            return;
        }

        setIsTemplateModalOpen(false);
        setIsExportingPDF(true);
        onExportStart?.();

        try {
            const { jsPDF } = await import('jspdf');
            const { renderTemplateToCanvas } = await import('@/services/label-print.service');

            const selectedItems = items.filter(item => selectedIds.has(item.id));
            const pdf = new jsPDF('p', 'mm', 'a4');
            let isFirstPage = true;

            for (const item of selectedItems) {
                if (!isFirstPage) {
                    pdf.addPage();
                }
                isFirstPage = false;

                let canvas: HTMLCanvasElement;
                try {
                    // 使用自定义渲染函数或默认渲染
                    if (renderLabel) {
                        canvas = await renderLabel(item, template);
                    } else {
                        const volWeight = ((item.length || 0) * (item.width || 0) * (item.height || 0)) / VOLUME_WEIGHT_DIVISOR;
                        const labelData = {
                            tracking_no: item.tracking_no,
                            receiver_name: item.receiver_name || '',
                            receiver_phone: item.receiver_phone || '',
                            receiver_address: item.receiver_address || '',
                            sender_name: item.shipper_name || '',
                            sender_phone: '',
                            sender_address: '',
                            weight: item.weight?.toString() || '',
                            volume_weight: volWeight.toFixed(2),
                            pieces: '1',
                            batch_no: batchNo,
                            remark: '',
                            date: new Date().toLocaleDateString('zh-CN'),
                            print_time: new Date().toLocaleString('zh-CN'),
                            operator: sanitizeInput(operatorName),
                        };
                        canvas = await renderTemplateToCanvas(template, labelData);
                    }

                    const imgData = canvas.toDataURL('image/png');
                    const labelWidth = parseInt(template.size.split('x')[0]);
                    const labelHeight = parseInt(template.size.split('x')[1]);
                    const x = (210 - labelWidth) / 2;
                    const y = 20;

                    pdf.addImage(imgData, 'PNG', x, y, labelWidth, labelHeight);

                    // 释放 Canvas 资源
                    canvas.width = 0;
                    canvas.height = 0;
                } catch (itemError) {
                    console.error(`[Batch Label Export] Failed to render item ${item.id}:`, itemError);
                    throw itemError;
                }
            }

            // 生成文件名
            const filename = `标签_${sanitizeFilename(batchNo)}_${new Date().toLocaleDateString('zh-CN').replace(/\//g, '-')}.pdf`;
            
            if ((window as any).Android) {
                // Android 环境使用原生桥接保存
                const pdfBase64 = pdf.output('datauristring');
                (window as any).Android.saveFile(pdfBase64, filename, "application/pdf");
            } else {
                // Web 环境直接保存
                pdf.save(filename);
            }

            toast.success(`已导出 ${selectedItems.length} 个标签PDF`);
            setIsSelectionMode(false);
            setSelectedIds(new Set());
            onExportComplete?.(selectedItems.length);
        } catch (err: any) {
            console.error('[Batch Label Export] Error:', err);
            
            // 详细的错误处理
            let errorMessage = '导出失败';
            if (err instanceof TypeError) {
                errorMessage = '数据格式错误';
            } else if (err.message?.includes('memory')) {
                errorMessage = '内存不足，请减少导出数量';
            } else if (err.message?.includes('timeout')) {
                errorMessage = '导出超时，请重试';
            } else {
                errorMessage = err.message || '未知错误';
            }
            
            toast.error(`导出失败: ${errorMessage}`);
            onExportError?.(new Error(errorMessage));
        } finally {
            setIsExportingPDF(false);
        }
    }, [selectedIds, items, batchNo, operatorName, renderLabel, onExportStart, onExportComplete, onExportError]);

    const handleExportClick = useCallback(() => {
        if (!selectedIds || selectedIds.size === 0) {
            toast.error('请先选择要导出的项目');
            return;
        }
        setIsTemplateModalOpen(true);
    }, [selectedIds]);

    return (
        <>
            {/* Header Controls */}
            <div className="flex items-center gap-2 flex-wrap">
                {!isSelectionMode ? (
                    <button
                        onClick={() => setIsSelectionMode(true)}
                        className="text-xs px-3 py-1.5 rounded-lg font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors flex items-center gap-1"
                    >
                        <span className="material-icons text-sm">check_box</span>
                        批量导出
                    </button>
                ) : (
                    <>
                        <button
                            onClick={toggleSelectAll}
                            className="text-xs px-3 py-1.5 rounded-lg font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                        >
                            {selectedIds.size === validItems.length ? '取消全选' : '全选'}
                        </button>
                        <button
                            onClick={() => {
                                setIsSelectionMode(false);
                                setSelectedIds(new Set());
                            }}
                            className="text-xs px-3 py-1.5 rounded-lg font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                        >
                            取消
                        </button>
                        <button
                            onClick={handleExportClick}
                            disabled={selectedIds.size === 0 || isExportingPDF}
                            className="text-xs px-3 py-1.5 rounded-lg font-medium bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                        >
                            <span className="material-icons text-sm">picture_as_pdf</span>
                            {isExportingPDF ? '导出中...' : `导出 (${selectedIds.size})`}
                        </button>
                    </>
                )}
            </div>

            {/* Selection State */}
            {isSelectionMode && (
                <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    已选择 {selectedIds.size} / {validItems.length} 项
                </div>
            )}

            {/* Template Modal */}
            {isTemplateModalOpen && (
                <TemplateSelectModal
                    isOpen={isTemplateModalOpen}
                    onClose={() => setIsTemplateModalOpen(false)}
                    onSelect={handleExportLabelsPDF}
                    selectedCount={selectedIds.size}
                />
            )}

            {/* Export State Context */}
            <div className="batch-label-export-context" data-selection-mode={isSelectionMode} data-selected-ids={Array.from(selectedIds).join(',')}>
                {/* This div provides context for child components to know selection state */}
            </div>
        </>
    );
};

// Hook to use BatchLabelExport state
export const useBatchLabelExport = () => {
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    const toggleSelection = useCallback((id: string) => {
        setSelectedIds(prev => {
            const newSet = new Set(prev);
            newSet.has(id) ? newSet.delete(id) : newSet.add(id);
            return newSet;
        });
    }, []);

    const isSelected = useCallback((id: string) => selectedIds.has(id), [selectedIds]);

    return {
        isSelectionMode,
        setIsSelectionMode,
        selectedIds,
        setSelectedIds,
        toggleSelection,
        isSelected,
    };
};
